import { create } from 'zustand';
import { toast } from 'react-toastify';
import {
  cancelFirmwareTestApi,
  excludeFirmwareTestDeviceApi,
  getFirmwareApi,
  getFirmwareTestApi,
  getTestDevicesApi,
  promoteFirmwareTestApi,
  startFirmwareTestApi,
  verifyFirmwareTestApi,
} from '../../../api/api';
import { getApiErrorMessage } from '../utils';

const EMPTY_STATUS = {
  campaign: null,
  stage: 0,
  target_version: '',
  total: 0,
  done: 0,
  pending: 0,
  excluded: 0,
  devices: [],
};

export const useFirmwareTestStore = create((set, get) => ({
  status: EMPTY_STATUS,
  firmware: [],
  testDevices: [],
  isLoading: false,
  error: null,
  firmwareError: null,
  actionLoading: false,
  excludingId: null,

  loadInitial: async () => {
    set({ isLoading: true, error: null });

    await getFirmwareTestApi({
      dataCb: (res) => set({ status: res ?? EMPTY_STATUS, isLoading: false }),
      errCb: (err) => set({ error: err, isLoading: false }),
    });

    await Promise.all([get().fetchTestDevices(), get().fetchFirmware()]);
  },

  fetchStatus: async ({ silent = false } = {}) => {
    if (!silent) set({ isLoading: true, error: null });

    await getFirmwareTestApi({
      dataCb: (res) => set({ status: res ?? EMPTY_STATUS, ...(silent ? {} : { isLoading: false }) }),
      errCb: (err) => set({ error: err, ...(silent ? {} : { isLoading: false }) }),
    });
  },

  fetchFirmware: async () => {
    try {
      const data = await getFirmwareApi({ channel: 'test' });
      set({ firmware: Array.isArray(data) ? data : [], firmwareError: null });
    } catch (err) {
      set({
        firmware: [],
        firmwareError: err?.response?.data || { error: 'Не удалось загрузить версии тестового канала' },
      });
    }
  },

  fetchTestDevices: async () => {
    await getTestDevicesApi({
      dataCb: (res) => set({ testDevices: Array.isArray(res) ? res : [] }),
      errCb: () => set({ testDevices: [] }),
    });
  },

  startCampaign: async (version) => {
    set({ actionLoading: true });

    try {
      const data = await startFirmwareTestApi({ version });
      set({ status: data ?? EMPTY_STATUS, actionLoading: false });
      toast.success(`Обкатка версии ${version} запущена`);
      return true;
    } catch (err) {
      set({ actionLoading: false });
      toast.error(getApiErrorMessage(err, 'Не удалось запустить обкатку'));
      return false;
    }
  },

  verifyCampaign: async (version) => {
    set({ actionLoading: true });

    try {
      const data = await verifyFirmwareTestApi({ version });
      set({ status: data ?? EMPTY_STATUS, actionLoading: false });
      toast.success(`Второй этап на версии ${version} запущен`);
      return true;
    } catch (err) {
      set({ actionLoading: false });
      toast.error(getApiErrorMessage(err, 'Не удалось запустить второй этап'));
      return false;
    }
  },

  promoteCampaign: async () => {
    set({ actionLoading: true });

    try {
      const data = await promoteFirmwareTestApi();
      const version = data?.version;
      const models = data?.models?.length ? ` для ${data.models.join(', ')}` : '';
      toast.success(version ? `Версия ${version} перенесена в стабильный канал${models}` : 'Прошивка перенесена в стабильный канал');
      set({ actionLoading: false });
      await get().loadInitial();
      return true;
    } catch (err) {
      set({ actionLoading: false });
      toast.error(getApiErrorMessage(err, 'Не удалось выкатить прошивку'));
      return false;
    }
  },

  cancelCampaign: async () => {
    set({ actionLoading: true });

    try {
      await cancelFirmwareTestApi();
      toast.success('Обкатка отменена');
      set({ actionLoading: false });
      await get().loadInitial();
      return true;
    } catch (err) {
      set({ actionLoading: false });
      toast.error(getApiErrorMessage(err, 'Не удалось отменить обкатку'));
      return false;
    }
  },

  excludeDevice: async (id) => {
    set({ excludingId: id });

    try {
      await excludeFirmwareTestDeviceApi({ id });
      toast.success('Устройство выведено из обкатки');
      set({ excludingId: null });
      await get().fetchStatus({ silent: true });
      return true;
    } catch (err) {
      set({ excludingId: null });
      toast.error(getApiErrorMessage(err, 'Не удалось вывести устройство из обкатки'));
      return false;
    }
  },
}));
